/**
 * An illuminated capital, drawn rather than stored.
 *
 * One parameterised SVG, varied by a hash of the chapter id — the same trick
 * as the character portraits, and for the same reason: twenty-eight hand-drawn
 * letters would be twenty-eight files to keep consistent, and a generator keeps
 * them a family by construction. Every letter gets the same vine, the same
 * gold, the same proportions, and a different arrangement of them.
 *
 * Unearned letters are drawn too, in outline. A blank square tells you nothing;
 * a ghost of the letter tells you there is something there to win.
 */

/** Deterministic per chapter, so a letter never changes under the reader. */
function hash(text) {
  let value = 0;
  for (let i = 0; i < text.length; i++) value = (value * 31 + text.charCodeAt(i)) >>> 0;
  return value;
}

const VINE_PATHS = [
  'M14 46 C 4 34, 10 16, 24 12 C 34 9, 42 16, 40 26',
  'M50 18 C 60 26, 58 44, 44 50 C 33 54, 24 47, 26 38',
  'M16 20 C 26 8, 46 10, 52 24 C 56 34, 50 42, 41 41',
  'M48 44 C 38 56, 18 54, 12 40 C 8 30, 15 21, 24 22',
];

const LEAF_ANGLES = [
  [30, 150, 250],
  [20, 110, 200, 300],
  [70, 190, 320],
  [45, 135, 225, 315],
];

export default function IlluminatedLetter({ letter, seed, earned, size = 64 }) {
  const h = hash(seed);
  // Unsigned shifts throughout. `>>` is signed, so any hash at or above 2^31
  // comes back negative, and a negative index is `undefined` rather than a
  // wrapped one — which is a blank letter for roughly half of them.
  const vine = VINE_PATHS[h % VINE_PATHS.length];
  const leaves = LEAF_ANGLES[(h >>> 3) % LEAF_ANGLES.length];
  const rotate = (h >>> 6) % 4;
  const corner = 6 + ((h >>> 9) % 5);
  const id = `ill-${seed}`;

  return (
    <svg
      className={`illum${earned ? ' is-earned' : ''}`}
      viewBox="0 0 64 64"
      width={size}
      height={size}
      role="img"
      aria-label={earned ? `Illuminated ${letter}` : `${letter}, not yet earned`}
    >
      <defs>
        <linearGradient id={`${id}-gold`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#e8d59a" />
          <stop offset="48%" stopColor="#c9a84c" />
          <stop offset="100%" stopColor="#8a6f28" />
        </linearGradient>
        <linearGradient id={`${id}-ground`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1b2236" />
          <stop offset="100%" stopColor="#0d1220" />
        </linearGradient>
      </defs>

      <g transform={`rotate(${rotate * 90} 32 32)`}>
        <rect
          x="2.5"
          y="2.5"
          width="59"
          height="59"
          rx={corner}
          fill={earned ? `url(#${id}-ground)` : 'transparent'}
          stroke={earned ? `url(#${id}-gold)` : 'currentColor'}
          strokeWidth="2"
          opacity={earned ? 1 : 0.3}
        />

        <path
          d={vine}
          fill="none"
          stroke={earned ? `url(#${id}-gold)` : 'currentColor'}
          strokeWidth="1.6"
          strokeLinecap="round"
          opacity={earned ? 0.85 : 0.18}
        />

        {leaves.map((angle, i) => (
          <ellipse
            key={i}
            cx="32"
            cy="10"
            rx="3.4"
            ry="6"
            transform={`rotate(${angle} 32 32)`}
            fill={earned ? `url(#${id}-gold)` : 'currentColor'}
            opacity={earned ? 0.55 : 0.14}
          />
        ))}
      </g>

      {/* The letter sits upright whatever the frame is doing. */}
      <text
        x="32"
        y="33"
        textAnchor="middle"
        dominantBaseline="central"
        className="illum__letter"
        fill={earned ? `url(#${id}-gold)` : 'currentColor'}
        opacity={earned ? 1 : 0.32}
      >
        {letter}
      </text>
    </svg>
  );
}
